import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import type { Service } from '../../lib/site';
import { shouldReduceExperience } from '../../lib/performance';
const accentMap = {
  blue: {
    chip: 'bg-royal/10 text-royal'
  },
  fire: {
    chip: 'bg-fire/10 text-fire'
  },
  green: {
    chip: 'bg-success/10 text-success'
  }
};
type ServiceCardProps = {
  service: Service;
  index?: number;
};
export function ServiceCard({
  service,
  index = 0
}: ServiceCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | null>(null);
  const pendingTilt = useRef({
    rotateX: 0,
    rotateY: 0
  });
  const [tiltEnabled, setTiltEnabled] = useState(false);
  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  const springX = useSpring(rotateX, {
    stiffness: 200,
    damping: 20
  });
  const springY = useSpring(rotateY, {
    stiffness: 200,
    damping: 20
  });
  const Icon = service.icon;
  const accent = accentMap[service.accent];
  useEffect(() => {
    setTiltEnabled(window.matchMedia('(pointer: fine)').matches && !shouldReduceExperience());
  }, []);
  const handleMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!tiltEnabled || !ref.current) return;
    const bounds = ref.current.getBoundingClientRect();
    const horizontal = (event.clientX - bounds.left) / bounds.width - 0.5;
    const vertical = (event.clientY - bounds.top) / bounds.height - 0.5;
    pendingTilt.current = {
      rotateX: -vertical * 8,
      rotateY: horizontal * 8
    };
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(() => {
      rotateX.set(pendingTilt.current.rotateX);
      rotateY.set(pendingTilt.current.rotateY);
      frameRef.current = null;
    });
  };
  const resetTilt = () => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    rotateX.set(0);
    rotateY.set(0);
  };
  return <motion.div initial={{
    opacity: 0,
    y: 40,
    filter: 'blur(10px)'
  }} whileInView={{
    opacity: 1,
    y: 0,
    filter: 'blur(0px)'
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.7,
    delay: index % 4 * 0.08,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-374" data-mp="2ijwn99i5m2re">
      <div style={{
      perspective: 1000
    }} data-id="element-375" data-mp="1yvxchynlavlz">
        <motion.div ref={ref} onMouseMove={handleMove} onMouseLeave={resetTilt} style={tiltEnabled ? {
        rotateX: springX,
        rotateY: springY,
        transformStyle: 'preserve-3d'
      } : undefined} data-cursor="button" className="group relative h-full" data-id="element-376" data-mp="1yy08r0fc5kz5">
          <Link to={`/services/${service.slug}`} className="gradient-border flex h-full flex-col rounded-4xl p-7 shadow-glass transition-all duration-500 hover:-translate-y-1.5 hover:rotate-[1.5deg] hover:shadow-glow" data-id="element-377" data-mp="3rtaysiwn4wjr">
            {service.emergency && <span className="absolute right-5 top-5 flex items-center gap-1.5 rounded-full bg-fire px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white" data-id="element-378" data-mp="acj9z9ku8reb">
                <span className="h-1.5 w-1.5 rounded-full bg-white animate-ping" data-id="element-379" data-mp="38w00p1mqw95w" />
                24/7
              </span>}
            <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${accent.chip} transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6`} data-id="element-380" data-mp="3irl679r0vrke">
              <Icon className="h-7 w-7" strokeWidth={1.8} data-id="element-381" data-mp="y0kg6bujnvqk" />
            </div>
            <h3 className="mt-6 font-display text-lg font-bold text-navy" data-id="element-382" data-mp="122o3pyuhmqs7">
              {service.title}
            </h3>
            <p className="mt-2 flex-1 text-sm leading-relaxed text-slate" data-id="element-383" data-mp="10n37jc7oqork">
              {service.short}
            </p>
            <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-royal" data-id="element-384" data-mp="voi99ibzuf5r">
              Learn more{' '}
              <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" data-id="element-385" data-mp="15l4s61krew55" />
            </span>
          </Link>
        </motion.div>
      </div>
    </motion.div>;
}